import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  CloudUpload as CloudUploadIcon,
  GraphicEq as AudioIcon,
  PhoneAndroid as AppIcon,
  PhotoLibrary as PhotoIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { DeploymentMarkerData } from 'src/view/Project/InteractiveMap';
import { UserRole } from 'src/api/Api';
import { useAppState } from 'src/state-management/state-provider';

interface DetectionItem {
  species: string;
  scientificName?: string;
  confidenceText: string;
  duration: string;
  imageUrl?: string | null;
  audioUrl?: string | null;
  waveform: number[];
  confidence: number;
}

interface AudiomothDetectionsPopupProps {
  open: boolean;
  onClose: () => void;
  siteName: string;
  deployment?: DeploymentMarkerData | null;
  deploymentId?: string | null;
  totalFiles?: number;
  onGetDetails?: () => void;
  onUploadData?: () => void;
  projectMedia?: any[];
  mediaUrls?: Record<string, string>;
}

const generateWaveform = (confidence = 0.8): number[] => {
  const base = [3, 8, 14, 18, 12, 7, 15, 10, 5, 2];
  const factor = Math.max(0.6, Math.min(1.2, confidence <= 1 ? confidence : confidence / 100));
  return base.map((h, i) => Math.max(2, Math.min(20, Math.round(h * factor + ((i % 3) - 1)))));
};

const AudiomothDetectionsPopup: React.FC<AudiomothDetectionsPopupProps> = ({
  open,
  onClose,
  siteName,
  deployment,
  deploymentId,
  totalFiles,
  onGetDetails,
  onUploadData,
  projectMedia = [],
  mediaUrls = {},
}) => {
  const [activeTab, setActiveTab] = useState<'audio' | 'appData'>('audio');
  const isAdmin = useAppState().state.user?.role?.includes(UserRole.Admin) ?? false;
  const [deploymentInfo, setDeploymentInfo] = useState<any>(null);
  const [retrievalInfo, setRetrievalInfo] = useState<any>(null);
  const [liveFileCount, setLiveFileCount] = useState<number | null>(null);

  const [detections, setDetections] = useState<DetectionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalSpeciesCount, setTotalSpeciesCount] = useState<number>(0);
  const [playingAudioUrl, setPlayingAudioUrl] = useState<string | null>(null);
  const [photoView, setPhotoView] = useState<{ label?: string; url?: string; date?: string } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const deploymentTitle = deployment?.deployment_name || 'Bioacoustic Sensor Unit';
  const effectiveDeploymentId = deploymentId || deployment?.deployment_id;


  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudioUrl(null);
    onClose();
  };

  const handleTogglePlay = (audioUrl?: string | null) => {
    if (!audioUrl) return;

    if (playingAudioUrl === audioUrl) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudioUrl(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const newAudio = new Audio(audioUrl);
    newAudio.crossOrigin = 'anonymous';
    audioRef.current = newAudio;
    setPlayingAudioUrl(audioUrl);

    newAudio.play().catch((err) => {
      console.error('Failed to play audio:', err);
      setPlayingAudioUrl(null);
    });

    newAudio.onended = () => {
      setPlayingAudioUrl(null);
      audioRef.current = null;
    };
  };

  // Fetch audio clips
  useEffect(() => {
    if (!open) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudioUrl(null);
      return;
    }

    if (!effectiveDeploymentId) {
      setDetections([]);
      setTotalSpeciesCount(0);
      return;
    }

    let isMounted = true;
    const fetchDetections = async () => {
      setDetections([]);
      setTotalSpeciesCount(0);
      setLoading(true);
      setError(null);
      try {
        const response = await clientService.request<Record<string, Record<string, unknown>[]>, unknown>({
          path: `/api/v1/deployments/${effectiveDeploymentId}/biodiversity/audio-clips`,
          method: 'GET',
          format: 'json',
          secure: true,
        });

        if (!isMounted) return;

        const data = response.data || {};
        const items: DetectionItem[] = [];

        const entries = Object.entries(data);

        entries.forEach(([commonName, clips]) => {
          if (Array.isArray(clips) && clips.length > 0) {
            const topClip = clips[0] as Record<string, unknown>;
            const rawConf = topClip.confidence;
            let conf = 0;
            let confText = '';

            if (typeof rawConf === 'number') {
              conf = rawConf;
            } else if (typeof rawConf === 'string') {
              const p = parseFloat(rawConf);
              if (!isNaN(p)) conf = p;
            } else if (Array.isArray(rawConf) && rawConf.length > 0) {
              const p = parseFloat(String(rawConf[0]));
              if (!isNaN(p)) conf = p;
            }

            if (conf > 0) {
              const pct = conf <= 1 ? conf * 100 : conf;
              confText = pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
            }

            let imgUrl = (topClip.image_url as string) || null;
            if (imgUrl && !imgUrl.startsWith('http')) {
              imgUrl = `https://storage.googleapis.com/mrv-data-bucket/${imgUrl}`;
            }

            items.push({
              species: (topClip.common_name as string) || commonName,
              scientificName: topClip.scientific_name as string | undefined,
              confidenceText: confText,
              duration: '00:03',
              imageUrl: imgUrl,
              audioUrl: (topClip.url as string) || null,
              waveform: generateWaveform(conf || 0.8),
              confidence: conf,
            });
          }
        });

        items.sort((a, b) => b.confidence - a.confidence);
        setDetections(items);
        setTotalSpeciesCount(items.length);
      } catch (err) {
        if (!isMounted) return;
        console.error('Failed to fetch deployment audio clips:', err);
        setError('Failed to load audio clips for this deployment.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDetections();

    return () => {
      isMounted = false;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [open, effectiveDeploymentId, totalFiles]);

  // Fetch deployment details / app data if deployment_id is provided
  useEffect(() => {
    if (!open || !effectiveDeploymentId) {
      setDeploymentInfo(null);
      setRetrievalInfo(null);
      setLiveFileCount(null);
      return;
    }

    const fetchDeploymentData = async () => {
      try {
        const infoRes = await clientService.request<any, any>({
          path: `/api/v1/admin/deployments/${effectiveDeploymentId}/info`,
          method: 'GET',
          format: 'json',
          secure: true,
        }).catch(() => null);

        const compRes = await clientService.request<any, any>({
          path: `/api/v1/admin/deployments/${effectiveDeploymentId}/comparison`,
          method: 'GET',
          format: 'json',
          secure: true,
        }).catch(() => null);

        if (compRes?.data) {
          if (compRes.data.deployment_info) {
            setDeploymentInfo(compRes.data.deployment_info);
          } else if (infoRes?.data) {
            setDeploymentInfo(infoRes.data);
          }
          if (compRes.data.retrieval_info) {
            setRetrievalInfo(compRes.data.retrieval_info);
          }
        } else if (infoRes?.data) {
          setDeploymentInfo(infoRes.data);
        }

        const detailRes = await clientService.request<any, any>({
          path: `/api/v1/admin/deployments/${effectiveDeploymentId}`,
          method: 'GET',
          format: 'json',
          secure: true,
        }).catch(() => null);

        if (detailRes?.data?.deployment) {
          const filesCount = detailRes.data.deployment.total_files ?? 0;
          setLiveFileCount(filesCount);
        }
      } catch (err) {
        console.error('Error fetching deployment details in popup:', err);
      }
    };

    fetchDeploymentData();
  }, [open, effectiveDeploymentId]);

  // Determine if this deployment has data
  const hasData = React.useMemo(() => {
    if (detections.length > 0) return true;
    if (liveFileCount !== null) {
      return liveFileCount > 0;
    }
    if (deployment?.total_files !== undefined) {
      return deployment.total_files > 0;
    }
    if (deployment?.total_audiofiles !== undefined) {
      return deployment.total_audiofiles > 0;
    }
    const status = deployment?.status?.toLowerCase();
    if (status === 'upcoming' || status === 'empty' || status === 'pending') {
      return false;
    }
    return true;
  }, [detections.length, liveFileCount, deployment]);

  const normalizedSiteName = siteName || deployment?.site_name || 'Alpha-7';
  const deviceTypeStr = (deployment?.device_type || 'Bioacoustic').toUpperCase();

  let audioFilesCount = '0';
  if (totalFiles !== undefined) {
    audioFilesCount = totalFiles.toLocaleString();
  } else if (liveFileCount !== null) {
    audioFilesCount = liveFileCount.toLocaleString();
  } else if (deployment?.total_files !== undefined) {
    audioFilesCount = deployment.total_files.toLocaleString();
  } else if (deployment?.total_audiofiles !== undefined) {
    audioFilesCount = deployment.total_audiofiles.toLocaleString();
  }

  // App data photos
  const { deploymentPhotos, retrievalPhotos } = React.useMemo(() => {
    const deployment: Array<{ label: string; url?: string; date?: string }> = [];
    const retrieval: Array<{ label: string; url?: string; date?: string }> = [];

    const formatUrl = (url: string) => {
      if (url.startsWith('http')) return url;
      return `https://storage.googleapis.com/mrv-data-bucket/${url}`;
    };

    const processInfoPhotos = (info: any, targetArray: any[]) => {
      if (!info) return;

      if (info.photo_urls && typeof info.photo_urls === 'object') {
        Object.entries(info.photo_urls).forEach(([key, val]) => {
          if (typeof val === 'string' && val.trim()) {
            const lbl = key.replace(/_/g, ' ').toUpperCase();
            targetArray.push({ label: lbl, url: formatUrl(val) });
          }
        });
      }

      if (info.flora_assessment && typeof info.flora_assessment === 'object') {
        const floraImgs = info.flora_assessment.image_urls || info.flora_assessment.images || info.flora_assessment.photos;
        if (Array.isArray(floraImgs)) {
          floraImgs.forEach((val: any, idx: number) => {
            if (typeof val === 'string' && val.trim()) {
              targetArray.push({ label: `FLORA ASSESSMENT ${idx + 1}`, url: formatUrl(val) });
            }
          });
        }
      }

      if (info.fauna_assessment && typeof info.fauna_assessment === 'object') {
        const faunaImgs = info.fauna_assessment.image_urls || info.fauna_assessment.images || info.fauna_assessment.photos;
        if (Array.isArray(faunaImgs)) {
          faunaImgs.forEach((val: any, idx: number) => {
            if (typeof val === 'string' && val.trim()) {
              targetArray.push({ label: `FAUNA ASSESSMENT ${idx + 1}`, url: formatUrl(val) });
            }
          });
        }
      }

      if (info.habitat_condition && typeof info.habitat_condition === 'object') {
        const habitatImgs = info.habitat_condition.image_urls || info.habitat_condition.photo_urls || info.habitat_condition.images;
        if (Array.isArray(habitatImgs)) {
          habitatImgs.forEach((val: any, idx: number) => {
            if (typeof val === 'string' && val.trim()) {
              targetArray.push({ label: `HABITAT CONDITION ${idx + 1}`, url: formatUrl(val) });
            }
          });
        }
      }
    };

    processInfoPhotos(deploymentInfo, deployment);
    processInfoPhotos(retrievalInfo, retrieval);

    if (projectMedia && projectMedia.length > 0) {
      projectMedia.forEach((m) => {
        const dUrl = mediaUrls[m.media_id];
        deployment.push({
          label: m.caption || m.file_name || 'FIELD PHOTO',
          url: dUrl,
          date: m.created_at ? new Date(m.created_at).toLocaleDateString() : undefined,
        });
      });
    }

    if (deployment.length === 0 && retrieval.length === 0) {
      deployment.push(
        {
          label: 'DEPLOYMENT SITE VIEW',
          date: 'RECENT CAPTURE',
          url: 'https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=400&q=80',
        },
        {
          label: 'CANOPY SENSOR SETUP',
          date: 'RECENT CAPTURE',
          url: 'https://images.unsplash.com/photo-1511497584788-87676104235f?auto=format&fit=crop&w=400&q=80',
        },
        {
          label: 'FIELD HABITAT SCAN',
          date: 'RECENT CAPTURE',
          url: 'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?auto=format&fit=crop&w=400&q=80',
        }
      );
    }
    return { deploymentPhotos: deployment, retrievalPhotos: retrieval };
  }, [deploymentInfo, retrievalInfo, projectMedia, mediaUrls]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #090E0B 0%, #030403 100%)',
          border: '1.5px solid #142218',
          borderRadius: '20px',
          color: '#fff',
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
          overflow: 'hidden',
          p: 2.5,
          boxShadow: '0 32px 80px rgba(0,0,0,0.85)',
        },
      }}
    >
      {/* Header bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#fff', letterSpacing: '0.5px' }}>
              {deploymentTitle}
            </Typography>
            <Chip
              label={deviceTypeStr}
              size="small"
              sx={{
                background: 'rgba(16, 185, 129, 0.12)',
                color: '#10b981',
                fontWeight: 800,
                fontSize: '9px',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                height: '20px',
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.55)', fontWeight: 600 }}>
            Site: <span style={{ color: '#fff' }}>{normalizedSiteName}</span>
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'rgba(255, 255, 255, 0.4)', p: 0.5, '&:hover': { color: '#fff' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Tabs Switcher: Acoustic Data vs Field App Data */}
      <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 2 }}>
          <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            minHeight: '36px',
            '& .MuiTabs-indicator': {
              backgroundColor: '#10b981',
              height: 2,
            },
          }}
        >
          <Tab
            value="audio"
            icon={<AudioIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
            label="Audio & Bioacoustics"
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'rgba(255, 255, 255, 0.5)',
              minHeight: '36px',
              py: 0.5,
              px: 1.5,
              '&.Mui-selected': { color: '#10b981' },
            }}
          />
          <Tab
            value="appData"
            icon={<AppIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
            label="Field App Data"
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'rgba(255, 255, 255, 0.5)',
              minHeight: '36px',
              py: 0.5,
              px: 1.5,
              '&.Mui-selected': { color: '#10b981' },
            }}
          />
        </Tabs>
      </Box>
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >


        {/* Tab 1: Audio / Bioacoustic Data */}
        {activeTab === 'audio' && (
          <>
            {/* Stats metrics row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: totalSpeciesCount > 0 ? '1fr 1fr' : '1fr', gap: 1.5, mb: 0.5 }}>
              <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.04)' }}>
                <Typography sx={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.35)', fontWeight: 700, letterSpacing: '1px', mb: 0.5 }}>
                  TOTAL AUDIO FILES
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AudioIcon sx={{ color: hasData ? '#10b981' : 'rgba(255,255,255,0.4)', fontSize: 14 }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 800, color: hasData ? '#fff' : 'rgba(255, 255, 255, 0.5)' }}>
                    {audioFilesCount}
                  </Typography>
                </Box>
              </Box>
              {totalSpeciesCount > 0 && (
                <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.04)' }}>
                  <Typography sx={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.35)', fontWeight: 700, letterSpacing: '1px', mb: 0.5 }}>
                    TOTAL SPECIES
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#fff' }}>
                      {totalSpeciesCount}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ color: '#10b981' }} />
              </Box>
            ) : error ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.4)' }}>
                  {error}
                </Typography>
              </Box>
            ) : detections.length > 0 ? (
              /* Case: Has real detections */
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)', fontWeight: 700, letterSpacing: '0.5px' }}>
                    SPECIES DETECTED
                  </Typography>
                </Box>

                {/* Species items list */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    maxHeight: '220px',
                    overflowY: 'auto',
                    pr: 0.5,
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' },
                  }}
                >
                  {detections.map((item, idx) => {
                    const isPlaying = playingAudioUrl === item.audioUrl;
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          bgcolor: isPlaying ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255, 255, 255, 0.02)',
                          borderRadius: '10px',
                          p: '6px 12px',
                          border: isPlaying ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(255, 255, 255, 0.04)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: isPlaying ? 'rgba(16, 185, 129, 0.12)' : 'rgba(255, 255, 255, 0.04)',
                            borderColor: 'rgba(16, 185, 129, 0.2)',
                          },
                        }}
                      >
                        {/* Play / Pause Button */}
                        <Box
                          onClick={() => handleTogglePlay(item.audioUrl)}
                          sx={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            bgcolor: item.audioUrl ? '#10b981' : 'rgba(255, 255, 255, 0.1)',
                            color: item.audioUrl ? '#000' : 'rgba(255, 255, 255, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: item.audioUrl ? 'pointer' : 'default',
                            flexShrink: 0,
                            transition: 'transform 0.15s, background-color 0.15s',
                            '&:hover': {
                              transform: item.audioUrl ? 'scale(1.08)' : 'none',
                              bgcolor: item.audioUrl ? '#0ea572' : 'rgba(255, 255, 255, 0.1)',
                            },
                          }}
                        >
                          {isPlaying ? (
                            <PauseIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <PlayIcon sx={{ fontSize: 16 }} />
                          )}
                        </Box>

                        {/* Title & sparkline Waveform */}
                        <Box sx={{ flex: 1, mx: 1.5, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 700,
                              color: '#fff',
                              mb: 0.25,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {item.species}
                            {item.confidenceText && (
                              <> - <span style={{ color: '#10b981' }}>Match: {item.confidenceText}</span></>
                            )}
                          </Typography>
                          {/* Mini audio bar graph */}
                          <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center', height: '12px' }}>
                            {item.waveform.map((h, barIdx) => (
                              <Box
                                key={barIdx}
                                sx={{
                                  width: '2px',
                                  height: isPlaying ? `${Math.min(18, Math.max(3, (h * (barIdx % 2 === 0 ? 1.3 : 0.7))))}px` : `${Math.max(3, h * 0.75)}px`,
                                  bgcolor: isPlaying ? '#34d399' : '#10b981',
                                  borderRadius: '0.5px',
                                  transition: 'height 0.2s',
                                }}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* Duration stamp & Profile photo */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                          <Typography sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 600 }}>
                            {item.duration}
                          </Typography>
                          {item.imageUrl ? (
                            <Box
                              component="img"
                              src={item.imageUrl}
                              alt={item.species}
                              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                e.currentTarget.style.display = 'none';
                              }}
                              sx={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '1.5px solid rgba(255, 255, 255, 0.12)',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                bgcolor: 'rgba(16, 185, 129, 0.15)',
                                color: '#10b981',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: 700,
                                border: '1.5px solid rgba(16, 185, 129, 0.3)',
                                flexShrink: 0,
                              }}
                            >
                              {item.species.charAt(0).toUpperCase()}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Bioacoustic Dashboard Button */}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={onGetDetails}
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#000',
                    fontWeight: 800,
                    fontSize: '12px',
                    py: 1.2,
                    borderRadius: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    boxShadow: '0 4px 20px rgba(16, 185, 129, 0.25)',
                    mt: 1,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0d9268 0%, #047857 100%)',
                      boxShadow: '0 4px 24px rgba(16, 185, 129, 0.4)',
                    },
                  }}
                >
                  Open Bioacoustic Dashboard &rarr;
                </Button>
              </>
            ) : hasData ? (
              /* Case: hasData but no specific species detections list -> Show Dashboard button */
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', mb: 2 }}>
                  Bioacoustic sensor is active. View recordings and analytics in the bioacoustic dashboard.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={onGetDetails}
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#000',
                    fontWeight: 800,
                    fontSize: '12px',
                    py: 1.2,
                    borderRadius: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    boxShadow: '0 4px 20px rgba(16, 185, 129, 0.25)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0d9268 0%, #047857 100%)',
                      boxShadow: '0 4px 24px rgba(16, 185, 129, 0.4)',
                    },
                  }}
                >
                  Open Bioacoustic Dashboard &rarr;
                </Button>
              </Box>
            ) : (
              /* Case: No Data -> Show empty state + Upload Data button */
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '14px',
                  border: '1px dashed rgba(255, 255, 255, 0.12)',
                  textAlign: 'center',
                  my: 1,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'rgba(16, 185, 129, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                    color: '#10b981',
                  }}
                >
                  <AudioIcon sx={{ fontSize: 24 }} />
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#fff', mb: 0.5 }}>
                  No Audio Recordings Available
                </Typography>
                <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', maxWidth: '340px', mb: 2.5 }}>
                  This deployment site has not uploaded raw audio recordings yet. Upload field acoustic files to initiate species identification.
                </Typography>

                {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={onUploadData}
                  sx={{
                    background: '#10b981',
                    color: '#000',
                    fontWeight: 800,
                    fontSize: '12px',
                    px: 3,
                    py: 1,
                    borderRadius: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background: '#0d9268',
                    },
                  }}
                >
                  Upload Data
                </Button>
                )}
              </Box>
            )}
          </>
        )}

        {/* Tab 2: Field App Data */}
        {activeTab === 'appData' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Field app entries: API values only */}
            {(() => {
              const rows: { label: string; value: string }[] = [];
              const flora = deploymentInfo?.flora_assessment?.dominant_species;
              if (flora) rows.push({ label: 'Flora', value: String(flora) });
              const fauna = deploymentInfo?.fauna_assessment?.evidence_type;
              if (fauna) rows.push({ label: 'Fauna', value: String(fauna) });
              const habitat = deploymentInfo?.habitat_condition?.health;
              if (habitat) rows.push({ label: 'Habitat', value: String(habitat) });
              if (rows.length === 0) return null;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {rows.map(r => (
                    <Box
                      key={r.label}
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: 2,
                        py: 0.75,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                      }}
                    >
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', color: 'rgba(255, 255, 255, 0.45)' }}>
                        {r.label}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#fff', textAlign: 'right' }}>
                        {r.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })()}

            {/* Field Photos Gallery */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                maxHeight: '260px',
                overflowY: 'auto',
                pr: 0.5,
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' },
              }}
            >
              {deploymentPhotos.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <PhotoIcon sx={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.6)' }} />
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255, 255, 255, 0.7)' }}>
                      FIELD-CAPTURED PHOTOS & DEPLOYMENT MEDIA
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
                    {deploymentPhotos.map((photo, pIdx) => (
                      <Box
                        key={pIdx}
                        onClick={() => photo.url && setPhotoView(photo)}
                        sx={{
                          height: '110px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundImage: photo.url ? `url(${photo.url})` : undefined,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'transform 0.2s, border-color 0.2s',
                          '&:hover': { transform: 'scale(1.02)', borderColor: 'rgba(16, 185, 129, 0.4)' },
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                            p: '6px 8px',
                          }}
                        >
                          {photo.date && (
                            <Typography sx={{ fontSize: '7.5px', color: 'rgba(255, 255, 255, 0.5)', mb: 0.25 }}>
                              {photo.date}
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              fontSize: '9px',
                              fontWeight: 700,
                              color: '#fff',
                              letterSpacing: '0.4px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {photo.label}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {retrievalPhotos.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <PhotoIcon sx={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.6)' }} />
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255, 255, 255, 0.7)' }}>
                      RETRIEVAL INFO MEDIA
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
                    {retrievalPhotos.map((photo, pIdx) => (
                      <Box
                        key={pIdx}
                        onClick={() => photo.url && setPhotoView(photo)}
                        sx={{
                          height: '110px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundImage: photo.url ? `url(${photo.url})` : undefined,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'transform 0.2s, border-color 0.2s',
                          '&:hover': { transform: 'scale(1.02)', borderColor: 'rgba(16, 185, 129, 0.4)' },
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                            p: '6px 8px',
                          }}
                        >
                          {photo.date && (
                            <Typography sx={{ fontSize: '7.5px', color: 'rgba(255, 255, 255, 0.5)', mb: 0.25 }}>
                              {photo.date}
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              fontSize: '9px',
                              fontWeight: 700,
                              color: '#fff',
                              letterSpacing: '0.4px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {photo.label}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Quick Upload action button from App Data tab */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
              {isAdmin && (
              <Button
                variant="outlined"
                fullWidth
                startIcon={<CloudUploadIcon />}
                onClick={onUploadData}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '11px',
                  fontWeight: 700,
                  py: 0.8,
                  borderRadius: '8px',
                  '&:hover': {
                    borderColor: '#10b981',
                    color: '#10b981',
                    background: 'rgba(16, 185, 129, 0.05)',
                  },
                }}
              >
                Upload Data
              </Button>
              )}
              {hasData && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={onGetDetails}
                  sx={{
                    background: '#10b981',
                    color: '#000',
                    fontSize: '11px',
                    fontWeight: 800,
                    py: 0.8,
                    borderRadius: '8px',
                    '&:hover': {
                      background: '#0d9268',
                    },
                  }}
                >
                  Bioacoustic Dashboard &rarr;
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* In-app photo viewer: image clicks no longer leave the popup */}
        <Dialog
          open={!!photoView}
          onClose={() => setPhotoView(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { bgcolor: '#000', position: 'relative', borderRadius: '10px' },
          }}
        >
          <IconButton
            onClick={() => setPhotoView(null)}
            sx={{ position: 'absolute', top: 6, right: 6, color: '#fff', zIndex: 1 }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          {photoView?.url && (
            <Box sx={{ p: 2 }}>
              <img
                src={photoView.url}
                alt={photoView.label || 'Field photo'}
                style={{ width: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: '8px' }}
              />
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: 'rgba(255, 255, 255, 0.7)',
                  mt: 1,
                  letterSpacing: '0.5px',
                }}
              >
                {photoView.label}
                {photoView.date ? ` · ${photoView.date}` : ''}
              </Typography>
            </Box>
          )}
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default AudiomothDetectionsPopup;
